// New branch… and Branch settings… of the history toolbar (spec/dashboard.md section 12.1, R-BR-01, Q-C-15, Q-C-16): the
// name checked inline against the rule of spec/api-conventions.md section 1, the start point (this branch's head, a
// commit, another branch); rename / description / Protected (`manage_settings`) with If-Match; Delete… with the typed name.
// New branch… is also opened by the branch selector (section 12.1) and by the protected-branch banner with `moveFrom`
// (section 4.3 step 6): the drafts of the protected branch move to the new branch (Workspace.moveDraftsTo, section 4.1).
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { Branch } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { gameKey, useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { Button } from '../../ui/Button';
import { useConfirm } from '../../ui/ConfirmDialog';
import { Field } from '../../ui/Field';
import { Select, Switch, TextArea, TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { useToasts } from '../../ui/Toast';
import { queryCache } from '../../workspace/cache';
import { useWorkspaceVersion, workspaceFor } from '../../workspace/react';
import type { Workspace } from '../../workspace/Workspace';
import { branchNameError, errorText } from './util';
import { LoadError } from '../../ui/PageStates';
import '../org/org.css';

export const PROTECTED_EXPLANATION = 'A protected branch takes no direct commits: changes land through merge requests, which need an approval.';

type FromMode = 'head' | 'commit' | 'branch';

export interface NewBranchDialogProps {
  open: boolean;
  onClose: () => void;
  branch: string;
  fromSha: string | null;
  /**
   * The workspace whose drafts move to the new branch (spec section 4.3 step 6, the protected-branch way out): the start
   * point is then fixed to this branch's head (or `fromSha`), and the dialog lands on the same page of the new branch.
   */
  moveFrom?: Workspace;
  /** Land on the same page of the new branch instead of its history (the branch selector's New branch…, section 12.1). */
  samePage?: boolean;
  /** Called after the branch is created (and the drafts moved), with its name. */
  onCreated?: (name: string) => void;
}

export function NewBranchDialog({ open, onClose, branch, fromSha, moveFrom, samePage, onCreated }: NewBranchDialogProps) {
  return (
    <Modal title="New branch" open={open} onClose={onClose} size="m">
      {open && <NewBranchBody branch={branch} fromSha={fromSha} onClose={onClose} moveFrom={moveFrom} samePage={samePage} onCreated={onCreated} />}
    </Modal>
  );
}

function NewBranchBody({ branch, fromSha, onClose, moveFrom, samePage, onCreated }: Omit<NewBranchDialogProps, 'open'>) {
  const { client } = useAuth();
  const { org, game, branches, missing, refreshBranches } = useGame();
  const toasts = useToasts();
  const navigate = useNavigate();
  const location = useLocation();
  useWorkspaceVersion(moveFrom ?? null);
  const moving = moveFrom ? moveFrom.changeCount : 0;
  // the page after the branch segment, kept when the dialog lands on the new branch (as BranchSelector.switchTo does)
  const samePageOn = (name: string): string => {
    const prefix = routes.branch(org, game.slug, branch, '');
    const rest = location.pathname.startsWith(prefix) ? location.pathname.slice(prefix.length) : 'templates';
    return routes.branch(org, game.slug, name, rest || 'templates') + location.search;
  };
  const [name, setName] = useState('');
  const [touched, setTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [mode, setMode] = useState<FromMode>(fromSha ? 'commit' : 'head');
  const [sha, setSha] = useState(fromSha ?? '');
  const others = branches.filter((b) => b.name !== branch);
  const [other, setOther] = useState(others[0]?.name ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameError = branchNameError(name, branches);
  const shaError = mode === 'commit' && !/^[0-9a-f]{4,64}$/i.test(sha.trim()) ? 'A commit sha (hex).' : null;
  // moving drafts fixes the start point: the drafts' base versions are this branch's head's
  const from = moveFrom ? (fromSha ?? branch) : mode === 'head' ? branch : mode === 'commit' ? sha.trim() : other;
  const valid = !nameError && (moveFrom ? true : !shaError) && from.length > 0;
  const submit = async () => {
    setTouched(true);
    if (!valid) return;
    setError(null);
    // a workspace this tab kept for a branch of the same name (deleted since) would be overwritten: refuse before creating
    if (moveFrom && moveFrom.isDirty && workspaceFor(game.id, name).isDirty) {
      setError(`This tab already holds unsaved changes for a branch named ${name}: pick another name.`);
      return;
    }
    setBusy(true);
    try {
      await client.data(ops.createBranch, { path: { org, game: game.slug }, body: { name, description: description.trim() || null, from } });
      // the game's branch list must hold the new branch before the navigation lands (the shell reads protection and the
      // head from it): invalidate the context's query and await the refetch, which the context's useQuery then shares
      const key = gameKey(org, game.slug, 'branches');
      queryCache.invalidate(key);
      try {
        await queryCache.get(key, () => client.all((cursor) => client.data(ops.listBranches, { path: { org, game: game.slug }, query: { limit: 200, cursor } })));
      } catch {
        refreshBranches(); // the context retries on its own; the navigation still lands
      }
      let moved = 0;
      if (moveFrom && moveFrom.isDirty) {
        moved = moveFrom.changeCount;
        moveFrom.moveDraftsTo(workspaceFor(game.id, name));
      }
      toasts.success(moveFrom ? `Branch ${name} created; ${moved} change${moved === 1 ? '' : 's'} moved.` : `Branch ${name} created.`);
      onClose();
      navigate(moveFrom || samePage ? samePageOn(name) : routes.history(org, game.slug, name));
      onCreated?.(name);
    } catch (e) {
      setError(e instanceof ApiError ? (e.fieldMessage('name') ?? errorText(e)) : errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      data-testid="new-branch-dialog"
      className="op-dialog"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {moveFrom && (
        <p className="new-branch-move" data-testid="new-branch-move">
          {moving > 0 ? `Your ${moving} unsaved change${moving === 1 ? '' : 's'} move to the new branch.` : 'No unsaved changes to move.'}
        </p>
      )}
      <Field label="Branch name" error={touched || name ? (nameError ?? undefined) : undefined} required>
        <TextInput value={name} onChange={(e) => setName(e.target.value)} onBlur={() => setTouched(true)} aria-label="Branch name" placeholder="feature/offers-v2" autoComplete="off" />
      </Field>
      <Field label="Description">
        <TextArea value={description} onChange={(e) => setDescription(e.target.value)} aria-label="Description" rows={2} />
      </Field>
      {moveFrom ? (
        <p className="muted small">
          Starts from {fromSha ? <span className="mono">{fromSha.slice(0, 8)}</span> : <>the head of {branch}</>}: the drafts keep their base versions there.
        </p>
      ) : (
      <Field label="Start from">
        <Select value={mode} onChange={(e) => setMode(e.target.value as FromMode)} aria-label="Start from">
          <option value="head">This branch’s head ({branch})</option>
          <option value="commit">A commit</option>
          <option value="branch" disabled={others.length === 0}>
            Another branch
          </option>
        </Select>
      </Field>
      )}
      {!moveFrom && mode === 'commit' && (
        <Field label="Commit sha" error={shaError ?? undefined}>
          <TextInput value={sha} onChange={(e) => setSha(e.target.value)} aria-label="Commit sha" className="mono" autoComplete="off" />
        </Field>
      )}
      {!moveFrom && mode === 'branch' && (
        <Field label="Source branch">
          <Select value={other} onChange={(e) => setOther(e.target.value)} aria-label="Source branch">
            {others.map((b) => (
              <option key={b.id} value={b.name}>
                {b.name}
              </option>
            ))}
          </Select>
        </Field>
      )}
      {error && (
        <LoadError>{error}</LoadError>
      )}
      <div className="modal-footer form-footer op-actions">
        <span className="spacer" />
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="primary" busy={busy} disabled={!valid} missingPermission={missing('branch_create')}>
          Create
        </Button>
      </div>
    </form>
  );
}

export function BranchSettingsDialog({ open, onClose, branch }: { open: boolean; onClose: () => void; branch: Branch | undefined }) {
  return (
    <Modal title={`Branch settings · ${branch?.name ?? ''}`} open={open} onClose={onClose} size="m">
      {open && branch && <SettingsBody branch={branch} onClose={onClose} />}
    </Modal>
  );
}

function SettingsBody({ branch, onClose }: { branch: Branch; onClose: () => void }) {
  const { client } = useAuth();
  const { org, game, branches, can, missing, refreshBranches } = useGame();
  const toasts = useToasts();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [name, setName] = useState(branch.name);
  const [description, setDescription] = useState(branch.description ?? '');
  const [isProtected, setProtected] = useState(branch.isProtected);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canEdit = can('content_write');
  // the PATCH itself needs content_write; changing the protection needs manage_settings on top (api-conventions 12.3)
  const canProtect = canEdit && can('manage_settings');
  const nameError = branchNameError(name, branches, branch.name);
  const patch: { name?: string; description?: string | null; isProtected?: boolean } = {};
  if (name !== branch.name) patch.name = name;
  if (description.trim() !== (branch.description ?? '')) patch.description = description.trim();
  if (isProtected !== branch.isProtected) patch.isProtected = isProtected;
  const dirty = Object.keys(patch).length > 0;
  const defaultBranch = branches.find((b) => b.isDefault);
  const deleteBlocked = branch.isDefault ? 'The default branch cannot be deleted.' : branch.isProtected ? 'A protected branch cannot be deleted: unprotect it first.' : undefined;

  const save = async () => {
    if (!dirty || nameError) return;
    setBusy(true);
    setError(null);
    try {
      await client.data(ops.updateBranch, { path: { org, game: game.slug, branch: branch.name }, body: patch, ifMatch: Number(branch.version) });
      toasts.success(patch.name ? `Branch renamed to ${patch.name}.` : 'Branch settings saved.');
      queryCache.invalidate(`branch:${game.id}:${branch.name}`);
      refreshBranches();
      onClose();
      if (patch.name) navigate(routes.history(org, game.slug, patch.name));
    } catch (e) {
      setError(e instanceof ApiError && e.isVersionConflict ? 'The branch changed since this dialog opened: close it and open it again.' : errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    const ok = await confirm({
      title: `Delete branch ${branch.name}?`,
      body: 'The branch and its head pointer are removed. Its commits stay reachable from the branches that contain them.',
      confirmLabel: 'Delete',
      tone: 'danger',
      typeToConfirm: branch.name,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await client.data(ops.deleteBranch, { path: { org, game: game.slug, branch: branch.name }, ifMatch: Number(branch.version) });
      toasts.success(`Branch ${branch.name} deleted.`);
      refreshBranches();
      onClose();
      if (defaultBranch) navigate(routes.history(org, game.slug, defaultBranch.name));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      data-testid="branch-settings-dialog"
      className="op-dialog"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <Field label="Branch name" error={nameError ?? undefined} help={branch.isDefault ? 'The default branch.' : undefined}>
        <TextInput value={name} onChange={(e) => setName(e.target.value)} aria-label="Branch name" disabled={!canEdit} autoComplete="off" />
      </Field>
      <Field label="Description">
        <TextArea value={description} onChange={(e) => setDescription(e.target.value)} aria-label="Description" rows={2} disabled={!canEdit} />
      </Field>
      <div className="branch-protect">
        <Switch label="Protected" checked={isProtected} onChange={setProtected} disabled={!canProtect} />
        <p className="muted small">{PROTECTED_EXPLANATION}</p>
        {!canProtect && <p className="muted small">You lack the &apos;{canEdit ? 'manage_settings' : 'content_write'}&apos; permission.</p>}
      </div>
      {error && (
        <LoadError>{error}</LoadError>
      )}
      <div className="modal-footer form-footer op-actions">
        <Button variant="danger" onClick={() => void remove()} disabled={!!deleteBlocked || busy} title={deleteBlocked} missingPermission={missing('branch_delete')}>
          Delete…
        </Button>
        <span className="spacer" />
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="primary" busy={busy} disabled={!dirty || !!nameError} missingPermission={missing('content_write')}>
          Save
        </Button>
      </div>
    </form>
  );
}
